import type { Components } from "@mui/material/styles";
// Importing this module is what adds `MuiPickerPopper` — and the rest of the pickers — to
// `Components`. It is type-only, so it costs nothing at runtime, and the augmentation is global
// once any file in the program pulls it in. `PickerComponents` is named only because a bare
// specifier-less import is not allowed here; nothing uses it.
import type { PickerComponents as _PickerComponents } from "@mui/x-date-pickers/themeAugmentation";

import type { Theme } from "../types";

/**
 * The surface every date picker opens onto.
 *
 * `@mui/x-date-pickers` renders its calendar in a `Popper`, not a `Popover`, so it inherited
 * nothing from `MuiPopover` — the one place this theme had said what a floating surface looks
 * like. What it fell back to was a `Paper` at `elevation={8}`, whose only lift in dark mode is a
 * white overlay `backgroundImage` that `MuiPaper` deletes app-wide, over a shadow that is pure
 * black at 50% on a near-black page. The result on the dark ground is a calendar with no readable
 * edge at all: the fields behind it stay legible straight through the popover.
 *
 * So this file says it explicitly, and says it the same way `MuiPopover` does — solid
 * `background.paper`, a `divider` hairline, a real shadow — so a calendar and a select dropdown
 * are recognisably the same kind of object.
 *
 * The z-index is raised past `zIndex.modal` on purpose. `PickerPopper` ships with
 * `zIndex: theme.zIndex.modal`, which is the *same* 1300 a `Dialog` uses, so a picker opened
 * inside a modal (the campaign create modal does exactly this) tied with its own backdrop and won
 * or lost on portal order. `zIndex.tooltip` is the layer above every dialog and keeps it above
 * both.
 */
export const MuiPickerPopper = {
	styleOverrides: {
		root: ({ theme }) => ({ zIndex: theme.zIndex.tooltip }),
		paper: ({ theme }) => ({
			backgroundColor: "var(--mui-palette-background-paper)",
			backgroundImage: "none",
			border: "1px solid var(--mui-palette-divider)",
			borderRadius: "12px",
			// The same elevation `MuiPopover` gives a select dropdown, read off the theme so the
			// two cannot drift and so it carries MUI's own literal fallback.
			boxShadow: theme.vars?.shadows[16] ?? theme.shadows[16],
			// The calendar header and action bar run to the edge; without this the square corners
			// of the first and last rows poke through the rounded ones.
			overflow: "hidden",
		}),
	},
} satisfies Components<Theme>["MuiPickerPopper"];
